"use client";

import React from "react";
import Link from "next/link";
import { ChevronDown, ChevronRight } from "lucide-react";
import { IconComponent } from "@repo/ui/components/icons";

/** One navigation menu node (server-shaped Navigation tree). */
export interface MenuTreeNode {
  _id: string;
  title?: { en?: string; mm?: string };
  url?: string;
  slug?: string;
  icon?: string;
  openInNewTab?: boolean;
  displayType?: "dropdown" | "mega";
  children?: MenuTreeNode[];
}

function pickLang(
  text: { en?: string; mm?: string } | string | undefined | null,
  lang: "en" | "mm",
): string {
  if (!text) return "";
  if (typeof text === "string") return text;
  return (text as any)[lang] || text.en || text.mm || "";
}

const glassPanel =
  "rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-card)]/95 shadow-2xl backdrop-blur-md";
const prismEdge: React.CSSProperties = {
  borderTopWidth: 2,
  borderTopColor: "transparent",
  backgroundImage:
    "linear-gradient(var(--color-card), var(--color-card)), var(--gradient-primary)",
  backgroundOrigin: "border-box",
  backgroundClip: "padding-box, border-box",
};

/**
 * Crystal top-level navigation item. Thin sans label with a prism hover
 * underline; children reveal a frosted-glass dropdown / mega panel on
 * hover (pure CSS `group-hover`, no JS).
 */
export function NavigationItem({
  item,
  currentLanguage,
}: {
  item: MenuTreeNode;
  currentLanguage: "en" | "mm";
}) {
  const label = pickLang(item.title, currentLanguage) || item.slug || "";
  const hasChildren = (item.children?.length ?? 0) > 0;
  const isMega = item.displayType === "mega";

  const linkClass =
    "crystal-nav-link group inline-flex items-center gap-1.5 px-1 py-2 text-[13.5px] font-medium text-[color:var(--color-muted-foreground)] transition-colors hover:text-[color:var(--color-foreground)]";

  const triggerInner = (
    <>
      {item.icon && (
        <IconComponent name={item.icon} size={15} className="flex-shrink-0" />
      )}
      <span className="relative">
        {label}
        <span
          className="pointer-events-none absolute -bottom-1 left-0 h-0.5 w-0 rounded-full transition-all duration-200 group-hover:w-full"
          style={{ background: "var(--gradient-primary)" }}
          aria-hidden="true"
        />
      </span>
      {hasChildren && (
        <ChevronDown
          className="h-3.5 w-3.5 opacity-70 transition-transform group-hover:rotate-180"
          aria-hidden="true"
        />
      )}
    </>
  );

  if (!hasChildren) {
    return (
      <Link
        href={item.url || "#"}
        target={item.openInNewTab ? "_blank" : undefined}
        rel={item.openInNewTab ? "noopener noreferrer" : undefined}
        className={linkClass}
      >
        {triggerInner}
      </Link>
    );
  }

  return (
    <div className={`group flex items-center ${isMega ? "" : "relative"}`}>
      <Link
        href={item.url || "#"}
        target={item.openInNewTab ? "_blank" : undefined}
        rel={item.openInNewTab ? "noopener noreferrer" : undefined}
        className={linkClass}
      >
        {triggerInner}
      </Link>

      {isMega ? (
        <div
          className="invisible absolute left-0 right-0 top-full z-40 px-4 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100 before:absolute before:bottom-full before:inset-x-0 before:h-3 before:content-['']"
          aria-label={`${label} menu`}
        >
          <div className={glassPanel} style={prismEdge}>
            <div className="grid grid-cols-2 gap-x-8 gap-y-6 p-6 md:grid-cols-3 lg:grid-cols-4">
              {item.children!.map((col) => {
                const colLabel =
                  pickLang(col.title, currentLanguage) || col.slug || "";
                return (
                  <div key={col._id} className="min-w-0">
                    <div className="crystal-eyebrow mb-2">{colLabel}</div>
                    <ul className="space-y-1.5">
                      {(col.children ?? []).map((leaf) => (
                        <li key={leaf._id}>
                          <Link
                            href={leaf.url || "#"}
                            target={leaf.openInNewTab ? "_blank" : undefined}
                            rel={
                              leaf.openInNewTab
                                ? "noopener noreferrer"
                                : undefined
                            }
                            className="inline-flex items-center gap-1.5 text-sm text-[color:var(--color-muted-foreground)] transition-colors hover:text-[color:var(--color-primary)]"
                          >
                            {leaf.icon && (
                              <IconComponent
                                name={leaf.icon}
                                size={14}
                                className="flex-shrink-0"
                              />
                            )}
                            {pickLang(leaf.title, currentLanguage) || leaf.slug}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div
          className="invisible absolute left-0 top-full z-40 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100 before:absolute before:bottom-full before:inset-x-0 before:h-2 before:content-['']"
          aria-label={`${label} submenu`}
        >
          <div className={`min-w-[240px] ${glassPanel}`} style={prismEdge}>
            <ul className="py-2">
              {item.children!.map((child) => (
                <DropdownNode
                  key={child._id}
                  node={child}
                  language={currentLanguage}
                />
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default NavigationItem;

/** Recursive dropdown row with a right-side flyout for grandchildren. */
function DropdownNode({
  node,
  language,
}: {
  node: MenuTreeNode;
  language: "en" | "mm";
}) {
  const label = pickLang(node.title, language) || node.slug || "";
  const hasChildren = (node.children?.length ?? 0) > 0;

  return (
    <li className={hasChildren ? "relative group/sub" : "relative"}>
      <Link
        href={node.url || "#"}
        target={node.openInNewTab ? "_blank" : undefined}
        rel={node.openInNewTab ? "noopener noreferrer" : undefined}
        className="flex items-center justify-between gap-2 px-4 py-2 text-sm text-[color:var(--color-muted-foreground)] transition-colors hover:bg-[color:var(--color-secondary)] hover:text-[color:var(--color-foreground)]"
      >
        <span className="inline-flex items-center gap-1.5">
          {node.icon && (
            <IconComponent name={node.icon} size={14} className="flex-shrink-0" />
          )}
          {label}
        </span>
        {hasChildren && (
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
        )}
      </Link>
      {hasChildren && (
        <div
          className="invisible absolute left-full top-0 z-50 pl-1 opacity-0 transition-all duration-150 group-hover/sub:visible group-hover/sub:opacity-100"
          aria-label={`${label} submenu`}
        >
          <div className={`min-w-[220px] ${glassPanel}`} style={prismEdge}>
            <ul className="py-2">
              {node.children!.map((child) => (
                <DropdownNode key={child._id} node={child} language={language} />
              ))}
            </ul>
          </div>
        </div>
      )}
    </li>
  );
}
